import { useMemo, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Search, ChevronRight, LayoutGrid } from "lucide-react";
import { OVERFLOW_CATEGORIES } from "@/lib/firm-navigation-catalog";
import { WORKSPACES } from "@/lib/workspace-registry";
import { Input } from "@/components/ui/input";
import { useSettings } from "@/lib/settings-store";
import { hasRoutePermission } from "@/lib/permissions";
import { useRoles } from "@/lib/rbac";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useLanguage } from "@/contexts/LanguageContext";

type ModuleRow = {
  id: string;
  label: string;
  to: string;
  search?: Record<string, unknown>;
  params?: Record<string, string>;
  group: string;
};

function buildModuleRows(): ModuleRow[] {
  const rows: ModuleRow[] = [];
  for (const ws of Object.values(WORKSPACES)) {
    for (const item of ws.items) {
      rows.push({
        id: `${ws.id}_${item.id}`,
        label: item.title,
        to: item.to,
        search: item.search,
        params: item.params,
        group: ws.title,
      });
    }
  }
  for (const cat of OVERFLOW_CATEGORIES) {
    for (const item of cat.items) {
      rows.push({
        id: `overflow_${item.id}`,
        label: item.label,
        to: item.to,
        search: item.search,
        group: cat.label,
      });
    }
  }
  return rows;
}

export function useAuthorizedModuleGroups(query = "") {
  const storedRole = useSettings((s) => s.currentUserRole);
  const { roles } = useRoles();
  const role = storedRole || roles[0] || "Owner";
  const q = query.trim().toLowerCase();

  return useMemo(() => {
    const permitted = buildModuleRows().filter((row) => hasRoutePermission(role, row.to));
    const filtered = q
      ? permitted.filter(
          (r) =>
            r.label.toLowerCase().includes(q) ||
            r.to.toLowerCase().includes(q) ||
            r.group.toLowerCase().includes(q),
        )
      : permitted;
    const byGroup = new Map<string, ModuleRow[]>();
    for (const row of filtered) {
      const list = byGroup.get(row.group) ?? [];
      list.push(row);
      byGroup.set(row.group, list);
    }
    return Array.from(byGroup.entries()).map(([group, items]) => ({
      id: group,
      label: group,
      items,
    }));
  }, [q, role]);
}

/** Full ERP module directory from workspace catalog + overflow. */
export function MobileModulesDirectory({
  onNavigate,
  searchable = true,
}: {
  onNavigate?: () => void;
  searchable?: boolean;
}) {
  const [query, setQuery] = useState("");
  const groups = useAuthorizedModuleGroups(query);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const { t } = useLanguage();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <div className="flex flex-col min-h-0">
      {searchable ? (
        <div className="relative px-1 pb-2">
          <Search className="absolute left-4 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter modules…"
            className="pl-9 min-h-[var(--touch-target)]"
            aria-label="Filter modules"
          />
        </div>
      ) : null}
      <nav className="flex-1 overflow-y-auto px-1 py-1" aria-label="ERP modules">
        {groups.length === 0 ? (
          <p className="px-3 py-6 text-sm text-muted-foreground text-center">
            {query.trim() ? "No modules match that filter." : "No modules available for this role."}
          </p>
        ) : (
          <>
            <p className="px-3 pb-2 text-[11px] text-muted-foreground">
              {total} module{total === 1 ? "" : "s"}
            </p>
            {groups.map((group) => (
              <div key={group.id} className="mb-3">
                <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
                  return (
                    <Link
                      key={item.id}
                      to={item.to as never}
                      search={(item.search ?? undefined) as never}
                      params={(item.params ?? undefined) as never}
                      onClick={onNavigate}
                      className={`flex items-center gap-2.5 min-h-[var(--touch-target)] rounded-sm px-2.5 py-2 border-b border-border/50 hover:bg-muted/60 active:bg-muted ${active ? "bg-gold/10" : ""}`}
                    >
                      <span className="flex-1 text-sm font-medium">{item.label}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </nav>
    </div>
  );
}

export function MobileModulesSheet({
  triggerClassName,
  onNavigate,
}: {
  triggerClassName?: string;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  function closeAndNavigate() {
    setOpen(false);
    onNavigate?.();
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className={
            triggerClassName ??
            "flex w-full items-center gap-3 min-h-[var(--touch-target)] rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium"
          }
        >
          <LayoutGrid className="h-4 w-4 text-gold" />
          <span className="flex-1 text-left">{t("mobile.allModules")}</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[min(90vh,720px)] rounded-t-xl p-0 flex flex-col">
        <SheetHeader className="px-4 pt-4 pb-2 border-b border-border shrink-0">
          <SheetTitle className="text-left font-serif">{t("mobile.allModules")}</SheetTitle>
          <p className="text-xs text-muted-foreground text-left">
            Workspace catalog — filter optional.
          </p>
        </SheetHeader>
        <div className="flex-1 min-h-0 px-3 pt-3 pb-[max(1rem,var(--ornexa-inset-bottom))]">
          <MobileModulesDirectory onNavigate={closeAndNavigate} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
